<template>
  <!-- 頁面標題 -->
  <div class="page-header">
    <h1>退換貨管理</h1>
  </div>

  <!-- 搜尋與篩選 -->
  <div class="search-filter">
    <div class="search-box">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="搜尋訂單編號或客戶名稱..."
        @input="handleSearch"
      />
    </div>

    <div class="filter-dropdown">
      <select v-model="activeFilter" class="form-input">
        <option
          v-for="filter in filters"
          :key="filter.value"
          :value="filter.value"
        >
          {{ filter.label }} ({{ filter.count ?? 0 }})
        </option>
      </select>
    </div>
  </div>

  <!-- 手機版 - 退貨卡片列表 -->
  <div class="return-list-mobile">
    <div
      class="return-card"
      v-for="returnItem in filteredReturns"
      :key="returnItem.id"
    >
      <!-- 退貨標題列 -->
      <div class="return-header">
        <div class="return-title-section">
          <div class="info-row">
            <span class="info-value">{{ returnItem.customerName }}</span>
          </div>
          <div class="badges-group">
            <span
              class="return-badge"
              :class="getStatusClass(returnItem.status)"
            >
              {{ returnItem.status }}
            </span>
            <span class="type-badge" :class="getTypeClass(returnItem.type)">
              {{ getTypeText(returnItem.type) }}
            </span>
          </div>
        </div>
        <!-- 操作按鈕 - 使用下拉選單 -->
        <div class="return-actions">
          <div class="return-actions-top">
            <button
              class="btn-mobile-action"
              @click="viewReturnDetail(returnItem)"
            >
              查看詳情
            </button>
          </div>
          <div class="dropdown-wrapper">
            <button
              class="btn-mobile-action status"
              @click.stop="toggleStatusDropdown(returnItem.id)"
            >
              更改狀態
            </button>
            <div
              class="dropdown-menu status-dropdown"
              v-if="activeStatusDropdown === returnItem.id"
              @click.stop
            >
              <button
                @click="
                  updateReturnStatus(returnItem, 'pending');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: returnItem.status === '審核中' }"
              >
                審核中
              </button>
              <button
                @click="
                  updateReturnStatus(returnItem, 'approved');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: returnItem.status === 'approved' }"
              >
                已同意
              </button>

              <button
                @click="
                  updateReturnStatus(returnItem, 'completed');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: returnItem.status === 'completed' }"
              >
                已完成
              </button>

              <button
                @click="
                  updateReturnStatus(returnItem, 'rejected');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: returnItem.status === 'rejected' }"
              >
                已拒絕
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 客戶與訂單資訊 -->
      <div class="return-info-section">
        <div class="info-row">
          <span class="info-label">原訂單</span>
          <span class="info-value">{{ returnItem.orderNumber }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">申請日期</span>
          <span class="info-value">{{ returnItem.applyDate }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">退款金額</span>
          <span class="info-value amount"
            >NT$ {{ formatNumber(returnItem.refundAmount) }}</span
          >
        </div>
      </div>

      <!-- 退貨商品 -->
      <div class="return-items">
        <div class="items-header">
          <span class="items-title">退貨商品</span>
        </div>
        <div class="items-list">
          <div class="item-row" v-for="item in returnItem.items" :key="item.id">
            <span class="item-name">{{ item.productName }}</span>
            <span class="item-quantity">x {{ item.quantity }}</span>
          </div>
        </div>
      </div>

      <!-- 退貨原因 -->
      <div class="return-reason" v-if="returnItem.reason">
        <div class="reason-label">退貨原因</div>
        <div class="reason-text">{{ returnItem.reason }}</div>
      </div>
    </div>
  </div>

  <!-- 手機版分頁按鈕區 -->
  <div class="pagination-container mobile-pagination">
    <button
      class="pagination-btn"
      :disabled="currentPage === 1"
      @click="prevPage"
    >
      ⬅ 上一頁
    </button>

    <span class="pagination-info">
      第 {{ currentPage }} / {{ totalPages }} 頁
    </span>

    <button
      class="pagination-btn"
      :disabled="currentPage === totalPages"
      @click="nextPage"
    >
      下一頁 ➡
    </button>
  </div>

  <!-- 電腦版 - 退貨表格 -->
  <div class="return-table-desktop">
    <div class="table-container">
      <table class="return-table">
        <thead>
          <tr>
            <th>退貨編號</th>
            <th>客戶名稱</th>
            <th>退/換貨類型</th>
            <th>退/換貨原因</th>
            <th>退貨商品</th>
            <th>退款金額</th>
            <th>申請日期</th>
            <th>狀態</th>
            <th style="text-align: center">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="returnItem in filteredReturns"
            :key="returnItem.id"
            class="table-row"
          >
            <td>
              <span
                class="order-link"
                @click="viewReturnDetail(returnItem)"
                title="點擊查看詳情"
              >
                {{ returnItem.returnNumber }}
              </span>
            </td>
            <td>
              <div class="customer-cell">
                <div class="customer-name">{{ returnItem.customerName }}</div>
                <div class="customer-phone">{{ returnItem.phone }}</div>
              </div>
            </td>
            <td>
              <span class="type-badge" :class="getTypeClass(returnItem.type)">
                {{ getTypeText(returnItem.type) }}
              </span>
            </td>
            <td class="reason-cell">
              <span class="reason-preview" :title="returnItem.reason">
                {{ truncateText(returnItem.reason, 30) }}
              </span>
            </td>
            <td class="items-cell">
              <button
                class="items-btn"
                @click="viewReturnDetail(returnItem)"
                :title="getItemsPreview(returnItem)"
              >
                {{ returnItem.items.length }} 項商品
              </button>
            </td>
            <td class="amount-cell">
              <span class="amount-text"
                >NT$ {{ formatNumber(returnItem.refundAmount) }}</span
              >
            </td>
            <td>
              <div class="date-cell">
                <div class="date-main">{{ returnItem.applyDate }}</div>
                <div class="date-sub" v-if="returnItem.processedDate">
                  處理: {{ returnItem.processedDate }}
                </div>
              </div>
            </td>
            <td>
              <span
                class="return-badge"
                :class="getStatusClass(returnItem.status)"
              >
                {{ returnItem.status }}
              </span>
            </td>
            <td class="actions-cell">
              <div class="table-actions">
                <button
                  class="btn-table-action"
                  @click="viewReturnDetail(returnItem)"
                  title="查看詳情"
                >
                  <img src="@/assets/image/icon/search.png" class="all-icon" />
                </button>

                <!-- 操作下拉選單 -->
                <div class="dropdown-wrapper">
                  <button
                    class="btn-table-action"
                    @click.stop="toggleStatusDropdown(returnItem.id)"
                    title="更改狀態"
                  >
                    <img src="@/assets/image/icon/return-box.png" class="all-icon" />
                  </button>
                  <div
                    class="dropdown-menu status-dropdown"
                    v-if="activeStatusDropdown === returnItem.id"
                    @click.stop
                  >
                    <button
                      @click="
                        updateReturnStatus(returnItem, 'pending');
                        activeStatusDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: returnItem.status === '審核中' }"
                    >
                      審核中
                    </button>

                    <button
                      @click="
                        updateReturnStatus(returnItem, 'approved');
                        activeStatusDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: returnItem.status === 'approved' }"
                    >
                      已同意
                    </button>

                    <button
                      @click="
                        updateReturnStatus(returnItem, 'completed');
                        activeStatusDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: returnItem.status === 'completed' }"
                    >
                      已完成
                    </button>

                    <button
                      @click="
                        updateReturnStatus(returnItem, 'rejected');
                        activeStatusDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: returnItem.status === 'rejected' }"
                    >
                      已拒絕
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- 查看退貨詳情 Modal -->
  <div class="modal" v-if="showDetailModal">
    <div class="modal-content large">
      <div class="modal-header">
        <h2>退貨詳情 - {{ selectedReturn?.customerName }}</h2>
        <button class="close-btn" @click="showDetailModal = false">×</button>
      </div>

      <div class="modal-body">
        <!-- 基本資訊 -->
        <div class="detail-section">
          <h3 class="section-title">📋 基本資訊</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">退貨編號</span>
              <span class="detail-value">{{
                selectedReturn?.returnNumber
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">訂單編號</span>
              <span class="detail-value">
                <span class="order-link" @click="viewOrder(selectedReturn)">
                  {{ selectedReturn?.orderNumber }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">退貨類型</span>
              <span class="detail-value">
                <span
                  class="type-badge"
                  :class="getTypeClass(selectedReturn?.type)"
                >
                  {{ getTypeText(selectedReturn?.type) }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">狀態</span>
              <span class="detail-value">
                <span
                  class="return-badge"
                  :class="getStatusClass(selectedReturn?.status)"
                >
                  {{ selectedReturn?.status }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">申請日期</span>
              <span class="detail-value">{{ selectedReturn?.applyDate }}</span>
            </div>
            <div class="detail-item" v-if="selectedReturn?.processedDate">
              <span class="detail-label">處理日期</span>
              <span class="detail-value">{{
                selectedReturn?.processedDate
              }}</span>
            </div>
          </div>
        </div>

        <!-- 客戶資訊 -->
        <div class="detail-section">
          <h3 class="section-title">👤 客戶資訊</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">客戶名稱</span>
              <span class="detail-value">{{
                selectedReturn?.customerName
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">聯絡電話</span>
              <span class="detail-value">{{ selectedReturn?.phone }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">聯絡人</span>
              <span class="detail-value">{{
                selectedReturn?.contactPerson
              }}</span>
            </div>
          </div>
        </div>

        <!-- 退貨商品明細 -->
        <div class="detail-section">
          <h3 class="section-title">📦 退貨商品明細</h3>
          <div class="detail-table">
            <table>
              <thead>
                <tr>
                  <th>商品名稱</th>
                  <th>規格</th>
                  <th>退貨數量</th>
                  <th>原價</th>
                  <th>退款金額</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="detail in selectedReturn?.returnDetails"
                  :key="detail.id"
                >
                  <td>{{ detail.productName }}</td>
                  <td>{{ detail.variantName || "-" }}</td>
                  <td>{{ detail.quantity }}</td>
                  <td>NT$ {{ formatNumber(detail.originalPrice) }}</td>
                  <td>NT$ {{ formatNumber(detail.refundAmount) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3" class="text-right">
                    <strong>退款總計</strong>
                  </td>
                  <td>
                    <strong
                      >NT$
                      {{ formatNumber(selectedReturn?.refundAmount) }}</strong
                    >
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- 退貨原因 -->
        <div class="detail-section">
          <h3 class="section-title">📝 退貨原因</h3>
          <div class="reason-content">
            {{ selectedReturn?.reason }}
          </div>
        </div>

        <!-- 退貨註記 (可編輯) -->
        <div class="detail-section">
          <h3 class="section-title">📌 退貨註記</h3>
          <div class="note-edit-container">
            <textarea
              v-model="selectedReturn.note"
              placeholder="輸入退貨相關註記事項..."
              class="note-textarea"
              rows="3"
            ></textarea>
            <div class="note-actions">
              <button
                class="btn-save-note"
                @click="saveReturnNote(selectedReturn)"
              >
                儲存註記
              </button>
            </div>
          </div>
        </div>

        <!-- 管理員回復 -->
        <!-- <div class="detail-section">
          <h3 class="section-title">💬 管理員回復</h3>
          <div class="admin-reply-container">
            <div
              class="admin-reply"
              v-if="selectedReturn?.adminReply"
            >
              <div class="reply-header">
                <div class="reply-meta">
                  <span class="reply-admin">{{ selectedReturn?.adminReply.adminName }}</span>
                  <span class="reply-date">{{ selectedReturn?.adminReply.replyDate }}</span>
                </div>
              </div>
              <div class="reply-content">
                {{ selectedReturn?.adminReply.message }}
              </div>
            </div>
            <div v-else class="no-reply">
              目前尚無管理員回復
            </div>
          </div>
        </div> -->

        <!-- 處理歷程 -->
        <div
          class="detail-section"
          v-if="selectedReturn?.history && selectedReturn?.history.length > 0"
        >
          <h3 class="section-title">📅 處理歷程</h3>
          <div class="timeline">
            <div
              class="timeline-item"
              v-for="(record, index) in selectedReturn?.history"
              :key="index"
            >
              <div class="timeline-dot" :class="record.type"></div>
              <div class="timeline-content">
                <div class="timeline-date">{{ record.date }}</div>
                <div class="timeline-action">{{ record.action }}</div>
                <div class="timeline-operator" v-if="record.operator">
                  操作人: {{ record.operator }}
                </div>
                <div class="timeline-note" v-if="record.note">
                  {{ record.note }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 拒絕退貨 Modal -->
  <div class="modal" v-if="showRejectModal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>拒絕退貨</h2>
        <button class="close-btn" @click="showRejectModal = false">×</button>
      </div>

      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">拒絕原因 *</label>
          <textarea
            class="form-input"
            v-model="rejectReason"
            placeholder="請輸入拒絕退貨的原因..."
            rows="5"
            maxlength="200"
          ></textarea>
          <div class="char-count">{{ rejectReason?.length || 0 }} / 200 字</div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="showRejectModal = false">
          取消
        </button>
        <button class="btn-submit btn-reject-modal" @click="confirmReject">
          確認拒絕
        </button>
      </div>
    </div>
  </div>
</template>

<script src="@/assets/js/return.js"></script>
<style scoped src="@/assets/css/return.css"></style>
